/**
 * The offline write queue — spec 10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE PRINCIPLE THIS FILE EXISTS TO KEEP
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * > *"The app's obligation is not to be online. It is to tell the truth about whether it
 * > is."* — spec 10 §1
 *
 * Two things follow, and everything below is one or the other:
 *
 *  1. **Nothing is lost.** A write made with no signal is durable on the phone before the
 *     call that made it returns, and it is replayed, in order, until the server has it.
 *  2. **Nothing is claimed.** A queued write never reports a server receipt, because it has
 *     none. Every caller is handed {@link WriteOutcome}, a two-armed type it cannot read
 *     without deciding which happened — there is no shape of this API that lets a screen
 *     print `Saved` over a note that is still sitting on the phone.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS QUEUED, AND WHY EACH
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * **Capture, Lock and Release — all three.** Spec 10 is not silent on this: §2.1's event log
 * names `note.locked` and `note.released` beside `note.created`; §4 requires a queued release
 * to be marked **distinctly** from a note nobody has pressed Release on; §5 step 5 makes
 * `Release Queued — 4 Notes Will Go When You're Back Online` an acceptance criterion; and
 * spec 03 §4 spells out the offline row of the Review & Release state table in the same
 * words. A release that refused to happen without signal would fail those four places at
 * once, and would do it at the desk after full time, which is exactly when the venue wifi is
 * worst.
 *
 * Queuing an act that carries judgement is only honest because the screen never pretends it
 * landed: a note whose release is queued **stays in the `Draft` section** with its own
 * `Release Queued` mark, and the receipt says `Release Queued`, never `Released`. Nothing
 * becomes irreversible offline that was not already irreversible online — this build has no
 * 15-minute undo window in either mode (spec 03 §2.4 step 5 is unbuilt, and ruling §H makes
 * Released permanent), so the queue adds no new one-way door. When the undo window is built
 * it lands here as spec 10 §6's row — *"the queued release event is cancelled outright and
 * never sent"* — which this queue can already express, because a queued write can be dropped
 * before it is drained.
 *
 * **The §H acknowledgement travels with the press.** `unlock` is stored on the queued release
 * itself, so the per-note answer the author gave to the warning is the answer that reaches
 * the API however long the queue holds it. A queued release can never acquire an
 * acknowledgement it was not given.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ORDER, IDEMPOTENCE AND THE REPLAY GUARD
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  * **Strictly ordered.** One monotonic sequence, drained head-first, one write at a time,
 *    stopping at the first write it cannot deliver. A capture therefore always reaches the
 *    server before the lock or the release that names the same note.
 *  * **Never overtaken.** While the queue is non-empty, a new write is queued too, even with
 *    a perfect connection. A write that jumped a queued predecessor would be a reordering,
 *    and the pair that reorders worst is capture-then-release.
 *  * **Idempotent by construction.** Note ids are minted on this device and `PUT /notes/:id`
 *    is a no-op on replay; `POST /notes/:id/lock` is idempotent; a release naming an
 *    already-released note releases nothing again. A retry can never duplicate an
 *    observation.
 *  * **The replay guard.** A capture receipt reports the **stored** visibility, not a guess
 *    (`CaptureReceiptBody`). If a replayed capture comes back `locked` or `released`, the
 *    server has moved that note on since the phone last saw it — so any queued `lock` for it
 *    is dropped rather than sent, because locking a released note is refused and re-locking a
 *    locked one is noise. The replay never resurrects or overwrites what the server holds:
 *    the `PUT` cannot, and this is the one place where the phone's own follow-up writes could
 *    have, so it is the one place that has to look.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IS DELIBERATELY NOT HERE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  * **No retry button, anywhere** (§1, §3). Not on the chip, not in the sheet, not hidden.
 *    Ruling Q-10a keeps a long-press retry *"for the TD only, for use during venue testing"*
 *    — and the UM app turns a TD away at `UmShell` before any of this renders, so that
 *    control would be unreachable code on this surface. It is not stubbed.
 *  * **No service worker, no Background Sync, no pull cursor** (§2.2, §2.3). The drain runs on
 *    `online`, on focus and on a 30-second foreground timer, which is precisely the iOS path
 *    §2.2 already prescribes; the rest belongs with the app-shell cache and is not faked.
 *  * **No pruning schedule** (Q-10b): nothing is retained after acknowledgement to prune. An
 *    acknowledged write is deleted, not marked.
 */

import type { CaptureNoteRequest } from "./types.js";
import {
  indexedDbQueueStorage,
  memoryQueueStorage,
  notesOf,
  type MemoryQueueStorage,
  type QueueStorage,
  type QueuedCapture,
  type QueuedWrite,
} from "./queueStore.js";

export type {
  QueuedCapture,
  QueuedFinalPush,
  QueuedLock,
  QueuedRelease,
  QueuedWrite,
} from "./queueStore.js";
export { notesOf } from "./queueStore.js";

/* ── What a caller is handed ──────────────────────────────────────────────── */

/**
 * The two-armed result of every note write in this application.
 *
 * There is no third arm and no optional field standing in for one: a caller must read
 * `sent` before it can reach either a receipt or a queued timestamp, so no screen can print
 * a server outcome it was not given. This is the type that makes §1's honesty a compile-time
 * property rather than a convention.
 */
export type WriteOutcome<T> =
  | { sent: true; receipt: T }
  | { sent: false; queuedAt: string; durable: boolean };

/* ── What the transport must answer ───────────────────────────────────────── */

/**
 * The result of one attempt to deliver one write, classified by the caller that owns the
 * error type (`client.ts`), so this module never has to know what an `ApiError` is.
 *
 *  * `sent` — delivered. `visibility` is a capture receipt's stored visibility, and drives
 *    the replay guard.
 *  * `unreachable` — transport, 5xx, 408, 429, **and 401/403**. The write STAYS QUEUED. A
 *    dead credential is a reason to stop trying, never a reason to throw an observation
 *    away: the device is re-signed-in from the same phone, and the queue is still there when
 *    it is.
 *  * `refused` — a considered answer from the server that repeating will not change (a §H
 *    `note_unlock_not_acknowledged`, a `note_already_released`). The write leaves the queue
 *    and is surfaced by name; it is never retried into an infinite loop and never dropped
 *    in silence.
 */
export type SendOutcome =
  | { result: "sent"; visibility?: "draft" | "locked" | "released" }
  | { result: "unreachable"; message: string }
  | { result: "refused"; code: string; message: string };

export type QueueSender = (write: QueuedWrite) => Promise<SendOutcome>;

/** A write the server answered with a refusal. Surfaced until the author has seen it. */
export interface RefusedWrite {
  write: QueuedWrite;
  code: string;
  message: string;
  refusedAt: string;
}

/* ── What a screen may read ───────────────────────────────────────────────── */

/** The three states of §3, and no fourth. */
export type SyncState = "synced" | "waiting" | "unreachable";

export interface QueueStatus {
  /**
   * **GATE G-18 lives in one line of code.** `synced` is computed from an empty queue and
   * from nothing else, so the green chip cannot render while anything is waiting.
   */
  state: SyncState;
  /** Distinct notes with anything unsent — the figure `6 Waiting To Send` counts (§5 step 3). */
  depth: number;
  /** Every unsent write, in replay order, for the sync detail sheet's per-note list (§3). */
  writes: readonly QueuedWrite[];
  pendingNoteIds: readonly string[];
  /** Notes whose **release** is queued — `Release Queued`, distinct from `Waiting To Send` (§4). */
  queuedReleaseNoteIds: readonly string[];
  /** Notes whose **lock** is queued. The author has said "this stays mine"; honour it locally. */
  queuedLockNoteIds: readonly string[];
  /**
   * Captures the server has not acknowledged, **carrying the whole note**.
   *
   * This is what makes "everything is still there" (§5 step 6) true without a materialised
   * local `notes` store: the screen renders these beside the server's own rows, so an
   * observation made with no signal is on screen from the moment it is made until long after
   * it has been delivered.
   */
  queuedCaptures: readonly QueuedCapture[];
  lastSyncAt: string | null;
  refused: readonly RefusedWrite[];
  /**
   * The phone refused to store something (Q-10c). The write is still held in memory and will
   * still be sent, but it would **not** survive a force-quit — and that is the one way a note
   * could genuinely be lost, so it is said out loud rather than swallowed.
   */
  storageFailed: boolean;
}

/* ── Back-off (§2.3) ──────────────────────────────────────────────────────── */

/** Immediate, 2 s, 5 s, 15 s, 60 s — then the two-minute plateau. */
export const BACKOFF_MS: readonly number[] = [0, 2000, 5000, 15000, 60000];
export const BACKOFF_PLATEAU_MS = 120000;
/** The foreground poll of §2.2, for browsers with no Background Sync (every iOS one). */
export const FOREGROUND_POLL_MS = 30000;

function delayFor(attempts: number): number {
  return BACKOFF_MS[attempts] ?? BACKOFF_PLATEAU_MS;
}

/** The ceiling of §3: no user-visible error until every back-off step has been spent. */
function ceilingReached(attempts: number): boolean {
  return attempts >= BACKOFF_MS.length;
}

/* ── Module state ─────────────────────────────────────────────────────────── */

type Scheduler = (run: () => void, ms: number) => () => void;

const defaultScheduler: Scheduler = (run, ms) => {
  const handle = setTimeout(run, ms);
  return () => {
    clearTimeout(handle);
  };
};

const defaultNow = (): string => new Date().toISOString();

const defaultMintId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const hex = (length: number): string =>
    Array.from({ length }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return `${hex(8)}-${hex(4)}-4${hex(3)}-8${hex(3)}-${hex(12)}`;
};

let storage: QueueStorage = indexedDbQueueStorage();
let sender: QueueSender | null = null;
let now: () => string = defaultNow;
let schedule: Scheduler = defaultScheduler;
let mintId: () => string = defaultMintId;

let writes: QueuedWrite[] = [];
let nextSeq = 1;
let lastSyncAt: string | null = null;
let reachable: "unknown" | "yes" | "no" = "unknown";
let refused: RefusedWrite[] = [];
let storageFailed = false;
let loaded: Promise<void> | null = null;
let draining = false;
let cancelRetry: (() => void) | null = null;
let device: string | null = null;

const listeners = new Set<() => void>();

/* ── Configuration seams ──────────────────────────────────────────────────── */

/** `client.ts` registers the transport at module load. Nothing else may. */
export function setQueueSender(next: QueueSender): void {
  sender = next;
}

export interface NoteQueueOptions {
  storage?: QueueStorage;
  now?: () => string;
  schedule?: Scheduler;
  mintId?: () => string;
}

/**
 * Reset every piece of module state and install whatever the caller wants in place of the
 * browser's. The suite calls this per test; nothing in the application does.
 *
 * The sender is deliberately **not** cleared — `client.ts` registers it once, at import.
 */
export function configureNoteQueue(options: NoteQueueOptions = {}): void {
  storage = options.storage ?? indexedDbQueueStorage();
  now = options.now ?? defaultNow;
  schedule = options.schedule ?? defaultScheduler;
  mintId = options.mintId ?? defaultMintId;

  cancelRetry?.();
  cancelRetry = null;
  writes = [];
  nextSeq = 1;
  lastSyncAt = null;
  reachable = "unknown";
  refused = [];
  storageFailed = false;
  loaded = null;
  draining = false;
  device = null;
  notify();
}

/** A fresh in-memory queue, installed and returned. jsdom ships no IndexedDB at all. */
export function installMemoryQueueStorage(
  initial: readonly QueuedWrite[] = [],
  options: Omit<NoteQueueOptions, "storage"> = {},
): MemoryQueueStorage {
  const fake = memoryQueueStorage(initial);
  configureNoteQueue({ ...options, storage: fake });
  return fake;
}

/* ── Subscription ─────────────────────────────────────────────────────────── */

export function subscribeQueue(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify(): void {
  for (const listener of listeners) listener();
}

/* ── Reading the queue ────────────────────────────────────────────────────── */

function distinct(values: readonly string[]): string[] {
  return [...new Set(values)];
}

/**
 * The status object. Recomputed rather than cached, and **`state` is derived from `depth`
 * before anything else is consulted** — gate G-18's promise, in the shape of the code.
 */
export function queueStatus(): QueueStatus {
  const pendingNoteIds = distinct(writes.flatMap((write) => [...notesOf(write)]));
  const state: SyncState =
    writes.length === 0 ? "synced" : reachable === "no" ? "unreachable" : "waiting";

  return {
    state,
    depth: pendingNoteIds.length,
    writes: writes.slice(),
    pendingNoteIds,
    queuedReleaseNoteIds: distinct(
      writes.filter((write) => write.kind === "release").flatMap((write) => [...notesOf(write)]),
    ),
    queuedLockNoteIds: distinct(
      writes.filter((write) => write.kind === "lock").flatMap((write) => [...notesOf(write)]),
    ),
    queuedCaptures: writes.filter((write): write is QueuedCapture => write.kind === "capture"),
    lastSyncAt,
    refused: refused.slice(),
    storageFailed,
  };
}

/** The count the chip renders. Zero means the green chip may render, and only then. */
export function queueDepth(): number {
  return writes.length;
}

/** Dismiss a surfaced refusal once the author has read it. It is never dismissed for them. */
export function acknowledgeRefusal(seq: number): void {
  refused = refused.filter((entry) => entry.write.seq !== seq);
  notify();
}

/**
 * Whether the browser says it has no network.
 *
 * `navigator.onLine === false` is trustworthy in the negative direction only — a `true` from
 * it means "attached to something", not "the server answers". So this is consulted to decide
 * whether to *bother* trying, and never to decide that a write succeeded. When it lies
 * optimistically the attempt simply fails and the write queues, which is the same outcome by
 * a slower road.
 */
export function isOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/* ── Loading ──────────────────────────────────────────────────────────────── */

export function loadNoteQueue(): Promise<void> {
  loaded ??= (async (): Promise<void> => {
    const stored = await storage.list();
    writes = stored;
    nextSeq = stored.reduce((highest, write) => Math.max(highest, write.seq), 0) + 1;
    device = await storage.deviceId(mintId);
    notify();
  })();
  return loaded;
}

/**
 * This device's id (spec 10 §2.1's `meta`), minted once and kept beside the queue.
 *
 * Synchronous, because a capture builds its body in one expression. Before the queue is
 * loaded it mints a value rather than returning null — a device id that changes across a
 * reload records which device wrote a note slightly less well; a null one would put a hole in
 * a NOT NULL column.
 */
export function deviceId(): string {
  device ??= mintId();
  return device;
}

/* ── Enqueuing ────────────────────────────────────────────────────────────── */

export type QueuedWriteDraft =
  | { kind: "capture"; noteId: string; note: CaptureNoteRequest }
  | { kind: "lock"; noteId: string }
  | { kind: "release"; matchId: number; notes: readonly string[]; unlock: readonly string[] }
  /** The Report Studio's single press — ONE unit, never two (um-app/06 §4.5). */
  | { kind: "finalPush"; tmsPersonId: string; pressId: string };

function materialise(draft: QueuedWriteDraft, seq: number, queuedAt: string): QueuedWrite {
  const base = { seq, queuedAt, attempts: 0, lastError: null };
  if (draft.kind === "capture") return { ...base, kind: "capture", noteId: draft.noteId, note: draft.note };
  if (draft.kind === "lock") return { ...base, kind: "lock", noteId: draft.noteId };
  if (draft.kind === "finalPush") {
    return {
      ...base,
      kind: "finalPush",
      tmsPersonId: draft.tmsPersonId,
      pressId: draft.pressId,
    };
  }
  return {
    ...base,
    kind: "release",
    matchId: draft.matchId,
    notes: [...draft.notes],
    unlock: [...draft.unlock],
  };
}

/**
 * Put a write on the queue and make it durable.
 *
 * The in-memory list is updated **whether or not** the store accepted it. A phone that has
 * run out of quota still sends this session's notes; what it has lost is their survival
 * across a force-quit, and that is reported through `storageFailed` rather than by throwing
 * the observation away. Losing a note is the one outcome this file exists to prevent.
 */
export async function enqueueWrite(draft: QueuedWriteDraft): Promise<QueuedWrite> {
  await loadNoteQueue();
  const write = materialise(draft, nextSeq, now());
  nextSeq += 1;
  writes = [...writes, write];

  try {
    await storage.put(write);
    storageFailed = false;
  } catch {
    storageFailed = true;
  }

  notify();
  return write;
}

/* ── Draining ─────────────────────────────────────────────────────────────── */

async function forget(write: QueuedWrite): Promise<void> {
  writes = writes.filter((existing) => existing.seq !== write.seq);
  await storage.remove(write.seq);
}

/**
 * The replay guard of the header. A capture that comes back already `locked` or `released`
 * means the server moved this note on while the phone was away, so the phone's own queued
 * `lock` for it is dropped rather than sent into a refusal.
 *
 * A queued **release** is deliberately left alone: releasing an already-released note is a
 * no-op the server answers cleanly, and silently removing a note from a press the author made
 * would be editing their decision rather than delivering it.
 */
async function guardAfterCapture(
  noteId: string,
  visibility: "draft" | "locked" | "released" | undefined,
): Promise<void> {
  if (visibility === undefined || visibility === "draft") return;
  const stale = writes.filter((write) => write.kind === "lock" && write.noteId === noteId);
  for (const write of stale) await forget(write);
}

function scheduleRetry(ms: number): void {
  cancelRetry?.();
  cancelRetry = schedule(() => {
    cancelRetry = null;
    void drainNoteQueue();
  }, ms);
}

/**
 * Replay the queue, head-first, until it is empty or one write cannot be delivered.
 *
 * One at a time and strictly in `seq` order — see the header on ordering. Re-entrant calls
 * return immediately rather than interleaving, because two drains racing would be exactly the
 * reordering the sequence number exists to prevent.
 */
export async function drainNoteQueue(): Promise<void> {
  if (draining) return;
  if (sender === null) return;

  // The flag is raised BEFORE the first await and not after it. Two callers that both got
  // past the guard while the other was awaiting the load would each drain the same head —
  // which is one observation delivered twice and, worse, two interleaved drains reordering
  // each other. It cost a test to find and one line to close.
  draining = true;
  const send = sender;
  try {
    await loadNoteQueue();
    if (writes.length === 0) return;

    while (writes.length > 0) {
      const head = writes[0]!;
      const outcome = await send(head);

      if (outcome.result === "sent") {
        await forget(head);
        lastSyncAt = now();
        reachable = "yes";
        if (head.kind === "capture") await guardAfterCapture(head.noteId, outcome.visibility);
        notify();
        continue;
      }

      if (outcome.result === "refused") {
        // A considered refusal. It leaves the queue — repeating it would only produce the
        // same answer — and it is surfaced by name, because a write that vanished quietly
        // would be a note the author believes was sent.
        await forget(head);
        refused = [
          ...refused,
          { write: head, code: outcome.code, message: outcome.message, refusedAt: now() },
        ];
        reachable = "yes";
        notify();
        continue;
      }

      // Unreachable. The write stays exactly where it is, at the head, and the whole queue
      // waits behind it — a later write that overtook it would be a reordering.
      const attempted: QueuedWrite = {
        ...head,
        attempts: head.attempts + 1,
        lastError: outcome.message,
      };
      writes = [attempted, ...writes.slice(1)];
      try {
        await storage.put(attempted);
      } catch {
        storageFailed = true;
      }
      if (ceilingReached(attempted.attempts)) reachable = "no";
      scheduleRetry(delayFor(attempted.attempts));
      notify();
      break;
    }

    if (writes.length === 0) {
      reachable = "yes";
      cancelRetry?.();
      cancelRetry = null;
    }
  } finally {
    draining = false;
    notify();
  }
}

/* ── The automatic drain (§2.2) ───────────────────────────────────────────── */

/**
 * Start the queue: load it, drain it, and keep draining it without anyone pressing anything.
 *
 * `online`, window focus, `visibilitychange` and a 30-second foreground timer — the iOS path
 * of §2.2, used everywhere because it is the one that works everywhere and because a:
 * Background Sync registration that silently did nothing on half the phones would be worse
 * than a timer that does the same thing on all of them.
 *
 * Returns its own teardown, so a re-render cannot leave two timers running.
 */
export function startNoteQueue(): () => void {
  let stopped = false;
  const kick = (): void => {
    if (!stopped && !isOffline()) void drainNoteQueue();
  };

  void loadNoteQueue().then(kick);

  // Re-armed rather than an interval, and the handle is replaced each time, so teardown
  // cancels the one that is actually pending.
  let cancelPoll: () => void = () => undefined;
  const poll = (): void => {
    if (stopped) return;
    kick();
    cancelPoll = schedule(poll, FOREGROUND_POLL_MS);
  };
  cancelPoll = schedule(poll, FOREGROUND_POLL_MS);

  if (typeof window !== "undefined") {
    window.addEventListener("online", kick);
    window.addEventListener("focus", kick);
    document.addEventListener("visibilitychange", kick);
  }

  return () => {
    stopped = true;
    cancelPoll();
    cancelPoll = () => undefined;
    cancelRetry?.();
    cancelRetry = null;
    if (typeof window !== "undefined") {
      window.removeEventListener("online", kick);
      window.removeEventListener("focus", kick);
      document.removeEventListener("visibilitychange", kick);
    }
  };
}
